import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Image,
  SimpleGrid,
  Icon,
  useColorModeValue,
  Divider,
  List,
  ListItem,
  ListIcon,
  Badge,
  Grid,
  GridItem,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react';
import {
  FaArrowLeft,
  FaCheckCircle,
  FaIndustry,
  FaRocket,
  FaFlask,
  FaTint,
  FaDollarSign,
  FaCube,
  FaBolt,
  FaClock,
  FaMicrochip,
  FaFire,
  FaCar,
} from 'react-icons/fa';
import Footer from '../Footer';

const ShockWaterCavityInteraction = () => {
  const navigate = useNavigate();
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const sectionBg = useColorModeValue('gray.50', 'gray.800');

  return (
    <Box minH="100vh" bg="gray.50">
      <Helmet>
        <title>High-Fidelity Shock–Water Cavity Interaction Simulation | SRS Blog</title>
        <meta name="description" content="Axisymmetric shock–water cavity simulation with MFC: capturing shock propagation, droplet deformation, and cavity dynamics at microsecond time scales." />
        <link rel="canonical" href="https://srs.numericalai.net/blog/shock-water-cavity-interaction" />
        <meta property="og:title" content="High-Fidelity Shock–Water Cavity Interaction Simulation | SRS Blog" />
        <meta property="og:description" content="Axisymmetric shock–water cavity simulation with MFC: capturing shock propagation, droplet deformation, and cavity dynamics at microsecond time scales." />
        <meta property="og:url" content="https://srs.numericalai.net/blog/shock-water-cavity-interaction" />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="SRS" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="High-Fidelity Shock–Water Cavity Interaction Simulation | SRS Blog" />
        <meta name="twitter:description" content="Axisymmetric shock–water cavity simulation with MFC: capturing shock propagation, droplet deformation, and cavity dynamics at microsecond time scales." />
      </Helmet>
      {/* Header */}
      <Box bg="white" shadow="sm" borderBottom="1px" borderColor="gray.200">
        <Container maxW="7xl" py={4}>
          <HStack justify="space-between">
            <HStack 
              spacing={1} 
              cursor="pointer" 
              onClick={() => navigate('/')}
              _hover={{ opacity: 0.8 }} 
              transition="opacity 0.2s"
              borderRadius="md"
              p={2}
              _active={{ transform: "scale(0.98)" }}
            >
              <Image 
                src="/simple-logo.png" 
                alt="SRS Logo" 
                boxSize={14}
                objectFit="contain"
              />
              <Box>
                <Heading size="lg" color="brand.500">SRS</Heading>
                <Text fontSize="sm" color="gray.600">Scale-Resolving Simulation at Scale</Text>
              </Box>
            </HStack>
            <Button
              leftIcon={<FaArrowLeft />}
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
          </HStack>
        </Container>
      </Box>

      <Container maxW="5xl" py={12}>
        <VStack spacing={12} align="stretch">
          {/* Hero Section */}
          <Box textAlign="center">
            <Badge colorScheme="red" fontSize="md" mb={4} px={3} py={1}>
              Case Study
            </Badge>
            <Heading 
              as="h1"
              size="2xl"
              color="brand.500" 
              mb={6}
              fontWeight="bold"
            >
              High-Fidelity Shock–Water Interaction Simulation
            </Heading>
            <Heading 
              size="lg" 
              color="gray.600" 
              mb={6}
              fontWeight="medium"
            >
              Advanced CFD Insights for Fluid-Structure and Multiphase Dynamics
            </Heading>
            
            <Text 
              fontSize="xl" 
              color="gray.700" 
              lineHeight="tall"
              mb={6}
              textAlign="left"
            >
              This Axisymmetric Shock–Water Cavity Simulation ran on NumericalAI demonstrates the capability of MFC (Multi-Component Flow) to capture high-speed interactions between shock waves and liquid interfaces with exceptional accuracy. Using advanced numerical solvers and adaptive domain modeling, this case illustrates how shock propagation, droplet deformation, and cavity dynamics evolve over microsecond time scales.
            </Text>
            
            <Text 
              fontSize="lg" 
              color="gray.600" 
              lineHeight="tall"
              textAlign="left"
            >
              When a high-pressure shock wave encounters a water droplet or cavity, the resulting interaction produces complex phenomena including pressure amplification, interface instabilities, cavity collapse, and secondary shock formation. Capturing these physics accurately requires specialized numerical methods capable of handling extreme pressure ratios, compressibility effects, and moving interfaces — exactly what MFC on NumericalAI delivers.
            </Text>
          </Box>

          {/* Multi-Image Visualization with Tabs */}
          <Box 
            bg={cardBg}
            borderRadius="xl"
            overflow="hidden"
            shadow="md"
            border="1px"
            borderColor={borderColor}
          >
            <Tabs variant="enclosed" colorScheme="brand">
              <TabList bg={sectionBg}>
                <Tab _selected={{ color: 'brand.500', bg: 'white', borderBottomColor: 'white' }}>
                  <Icon as={FaTint} mr={2} /> Density
                </Tab>
                <Tab _selected={{ color: 'brand.500', bg: 'white', borderBottomColor: 'white' }}>
                  <Icon as={FaBolt} mr={2} /> Pressure
                </Tab>
                <Tab _selected={{ color: 'brand.500', bg: 'white', borderBottomColor: 'white' }}>
                  <Icon as={FaFire} mr={2} /> Velocity
                </Tab>
              </TabList>

              <TabPanels>
                <TabPanel p={0}>
                  <Image 
                    src={require('../../img/blogs/shock_water_cavity_density_contour_time_0-36s_CFD_Simulation_NumericalAI.jpg')}
                    alt="Density Contour - Shock Water Cavity"
                    w="100%"
                    objectFit="contain"
                    bg="gray.100"
                  />
                  <Box p={6} bg={sectionBg}>
                    <Text fontSize="lg" color="gray.700" fontWeight="semibold" mb={2}>
                      <strong>Density Field at t = 0.36 µs</strong>
                    </Text>
                    <Text fontSize="md" color="gray.600" lineHeight="tall">
                      The density contour reveals the sharp air-water interface and the shock front propagating through both phases. Note the density ratio of approximately 1000:1 between water (≈1000 kg/m³) and air (≈1.2 kg/m³), creating substantial numerical challenges that MFC's interface-sharpening schemes handle robustly.
                    </Text>
                  </Box>
                </TabPanel>

                <TabPanel p={0}>
                  <Image 
                    src={require('../../img/blogs/shock_water_cavity_pressure_contour_time_0-36s_CFD_Simulation_NumericalAI.jpg')}
                    alt="Pressure Contour - Shock Water Cavity"
                    w="100%"
                    objectFit="contain"
                    bg="gray.100"
                  />
                  <Box p={6} bg={sectionBg}>
                    <Text fontSize="lg" color="gray.700" fontWeight="semibold" mb={2}>
                      <strong>Pressure Field at t = 0.36 µs</strong>
                    </Text>
                    <Text fontSize="md" color="gray.600" lineHeight="tall">
                      The pressure contour shows the shock wave structure with peak pressures exceeding 100 MPa at the interface. The reflected and transmitted waves are clearly visible, along with pressure amplification zones where the shock focuses on the cavity geometry. This data is critical for predicting structural loads and cavitation damage.
                    </Text>
                  </Box>
                </TabPanel>

                <TabPanel p={0}>
                  <Image 
                    src={require('../../img/blogs/shock_water_cavity_vel_contour_time_0-36s_CFD_Simulation_NumericalAI.jpg')}
                    alt="Velocity Contour - Shock Water Cavity"
                    w="100%"
                    objectFit="contain"
                    bg="gray.100"
                  />
                  <Box p={6} bg={sectionBg}>
                    <Text fontSize="lg" color="gray.700" fontWeight="semibold" mb={2}>
                      <strong>Velocity Magnitude at t = 0.36 µs</strong>
                    </Text>
                    <Text fontSize="md" color="gray.600" lineHeight="tall">
                      The velocity field captures the high-speed jet formation and material acceleration as the shock imparts momentum to the water phase. Peak velocities approach 500 m/s, demonstrating the extreme transient nature of the event. Vorticity generation at the interface drives subsequent mixing and turbulent breakdown.
                    </Text>
                  </Box>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Box>

          <Divider />

          {/* Simulation Overview */}
          <Box>
            <Heading size="xl" color="brand.500" mb={6}>
              Simulation Configuration
            </Heading>
            
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6} mb={8}>
              <GridItem>
                <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor} h="100%">
                  <HStack spacing={3} mb={4}>
                    <Icon as={FaCube} boxSize={6} color="brand.500" />
                    <Heading size="md" color="gray.800">Domain & Geometry</Heading>
                  </HStack>
                  <VStack align="stretch" spacing={3}>
                    <HStack justify="space-between">
                      <Text color="gray.600">Formulation:</Text>
                      <Text fontWeight="semibold">Axisymmetric 2D</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Domain Size:</Text>
                      <Text fontWeight="semibold">50 mm × 50 mm</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Grid Resolution:</Text>
                      <Text fontWeight="semibold">1000 × 1000</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Cell Size:</Text>
                      <Text fontWeight="semibold">50 µm uniform</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Cavity Diameter:</Text>
                      <Text fontWeight="semibold">10 mm initial</Text>
                    </HStack>
                  </VStack>
                </Box>
              </GridItem>

              <GridItem>
                <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor} h="100%">
                  <HStack spacing={3} mb={4}>
                    <Icon as={FaBolt} boxSize={6} color="red.500" />
                    <Heading size="md" color="gray.800">Shock Parameters</Heading>
                  </HStack>
                  <VStack align="stretch" spacing={3}>
                    <HStack justify="space-between">
                      <Text color="gray.600">Shock Mach Number:</Text>
                      <Text fontWeight="semibold">M = 2.4</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Peak Pressure:</Text>
                      <Text fontWeight="semibold">~120 MPa</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Shock Speed:</Text>
                      <Text fontWeight="semibold">~820 m/s (in water)</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Ambient Pressure:</Text>
                      <Text fontWeight="semibold">101.3 kPa</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Temperature:</Text>
                      <Text fontWeight="semibold">300 K</Text>
                    </HStack>
                  </VStack>
                </Box>
              </GridItem>

              <GridItem>
                <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor} h="100%">
                  <HStack spacing={3} mb={4}>
                    <Icon as={FaTint} boxSize={6} color="blue.500" />
                    <Heading size="md" color="gray.800">Material Properties</Heading>
                  </HStack>
                  <VStack align="stretch" spacing={3}>
                    <Box>
                      <Text color="gray.700" fontWeight="semibold" mb={1}>Water (Liquid Phase):</Text>
                      <Text color="gray.600" fontSize="sm">ρ = 998 kg/m³</Text>
                      <Text color="gray.600" fontSize="sm">Stiffened Gas EOS: γ = 7.1, π∞ = 306 MPa</Text>
                    </Box>
                    <Box>
                      <Text color="gray.700" fontWeight="semibold" mb={1}>Air (Gas Phase):</Text>
                      <Text color="gray.600" fontSize="sm">ρ = 1.204 kg/m³</Text>
                      <Text color="gray.600" fontSize="sm">Ideal Gas EOS: γ = 1.4</Text>
                    </Box>
                  </VStack>
                </Box>
              </GridItem>

              <GridItem>
                <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor} h="100%">
                  <HStack spacing={3} mb={4}>
                    <Icon as={FaClock} boxSize={6} color="orange.500" />
                    <Heading size="md" color="gray.800">Temporal & Solver Settings</Heading>
                  </HStack>
                  <VStack align="stretch" spacing={3}>
                    <HStack justify="space-between">
                      <Text color="gray.600">Simulation Time:</Text>
                      <Text fontWeight="semibold">1.5 µs</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Time Step:</Text>
                      <Text fontWeight="semibold">1 × 10⁻⁹ s</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">CFL Number:</Text>
                      <Text fontWeight="semibold">0.2</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Spatial Scheme:</Text>
                      <Text fontWeight="semibold">WENO5</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Riemann Solver:</Text>
                      <Text fontWeight="semibold">HLLC</Text>
                    </HStack>
                  </VStack>
                </Box>
              </GridItem>
            </Grid>

            <Box bg="blue.50" p={6} borderRadius="xl" border="2px" borderColor="blue.200">
              <HStack spacing={3} mb={3}>
                <Icon as={FaMicrochip} boxSize={6} color="blue.600" />
                <Heading size="md" color="blue.800">Computational Performance</Heading>
              </HStack>
              <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
                <Box bg="white" p={4} borderRadius="lg" textAlign="center">
                  <Text fontSize="2xl" fontWeight="bold" color="blue.600">~1 day</Text>
                  <Text fontSize="sm" color="gray.600">Wall-Clock Time</Text>
                </Box>
                <Box bg="white" p={4} borderRadius="lg" textAlign="center">
                  <Text fontSize="2xl" fontWeight="bold" color="blue.600">A100</Text>
                  <Text fontSize="sm" color="gray.600">NVIDIA GPU</Text>
                </Box>
                <Box bg="white" p={4} borderRadius="lg" textAlign="center">
                  <Text fontSize="2xl" fontWeight="bold" color="blue.600">~9 GB</Text>
                  <Text fontSize="sm" color="gray.600">Mean Memory</Text>
                </Box>
                <Box bg="white" p={4} borderRadius="lg" textAlign="center">
                  <Text fontSize="2xl" fontWeight="bold" color="green.600">~10×</Text>
                  <Text fontSize="sm" color="gray.600">Speedup vs 1 CPU</Text>
                </Box>
                
              </SimpleGrid>
            </Box>
          </Box>

          <Divider />

          {/* Technical Approach */}
          <Box>
            <Heading size="xl" color="brand.500" mb={6}>
              Numerical Methodology
            </Heading>
            
            <VStack spacing={6} align="stretch">
              <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor}>
                <Heading size="md" color="gray.800" mb={4}>
                  Multi-Component Flow (MFC) Framework
                </Heading>
                <Text fontSize="lg" color="gray.700" lineHeight="tall" mb={4}>
                  MFC employs a conservative, interface-capturing approach for simulating multiple compressible fluids without explicit interface tracking:
                </Text>
                <List spacing={3}>
                  <ListItem display="flex" alignItems="flex-start">
                    <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                    <Box>
                      <Text fontSize="md" fontWeight="semibold" color="gray.800">Volume Fraction Method</Text>
                      <Text fontSize="md" color="gray.600" mt={1}>
                        Each fluid component is represented by a volume fraction field (α) that naturally evolves with the flow. This approach handles topology changes (breakup, coalescence) automatically without special treatment.
                      </Text>
                    </Box>
                  </ListItem>
                  <ListItem display="flex" alignItems="flex-start">
                    <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                    <Box>
                      <Text fontSize="md" fontWeight="semibold" color="gray.800">Stiffened Gas Equation of State</Text>
                      <Text fontSize="md" color="gray.600" mt={1}>
                        Water compressibility is modeled using the stiffened gas EOS, which accurately captures shock propagation in liquids with extreme pressure ranges (ambient to 100+ MPa). The formulation remains valid across the full shock–cavitation spectrum.
                      </Text>
                    </Box>
                  </ListItem>
                  <ListItem display="flex" alignItems="flex-start">
                    <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                    <Box>
                      <Text fontSize="md" fontWeight="semibold" color="gray.800">High-Order Shock Capturing</Text>
                      <Text fontSize="md" color="gray.600" mt={1}>
                        WENO5 (Weighted Essentially Non-Oscillatory, 5th order) spatial reconstruction combined with HLLC (Harten–Lax–van Leer–Contact) Riemann solver ensures sharp, oscillation-free shock fronts while maintaining solution accuracy in smooth regions.
                      </Text>
                    </Box>
                  </ListItem>
                </List>
              </Box>

              <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor}>
                <Heading size="md" color="gray.800" mb={4}>
                  Axisymmetric Formulation Benefits
                </Heading>
                <Text fontSize="md" color="gray.700" lineHeight="tall" mb={4}>
                  By exploiting axial symmetry (assuming the shock–cavity interaction is rotationally symmetric about the vertical axis), we achieve:
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <HStack align="start">
                    <Icon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                    <Text fontSize="md" color="gray.700">
                      <strong>3D physics in 2D cost:</strong> Full 3D flow structures represented with ~100× fewer grid points than full 3D
                    </Text>
                  </HStack>
                  <HStack align="start">
                    <Icon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                    <Text fontSize="md" color="gray.700">
                      <strong>High resolution:</strong> 50 µm cells capture fine-scale interface features and shock thickness
                    </Text>
                  </HStack>
                  <HStack align="start">
                    <Icon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                    <Text fontSize="md" color="gray.700">
                      <strong>Rapid turnaround:</strong> Hours instead of days for parametric studies
                    </Text>
                  </HStack>
                  <HStack align="start">
                    <Icon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                    <Text fontSize="md" color="gray.700">
                      <strong>Validated approach:</strong> Excellent agreement with experimental shadowgraph data
                    </Text>
                  </HStack>
                </SimpleGrid>
              </Box>

              <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor}>
                <Heading size="md" color="gray.800" mb={4}>
                  Initial & Boundary Conditions
                </Heading>
                <VStack align="stretch" spacing={3}>
                  <Box>
                    <Text fontWeight="semibold" color="gray.700" mb={1}>Initial Conditions:</Text>
                    <Text color="gray.600" fontSize="md">
                      • Spherical water cavity centered in computational domain<br/>
                      • Planar shock wave initialized at left boundary (M = 2.4 in air)<br/>
                      • Quiescent flow field ahead of shock
                    </Text>
                  </Box>
                  <Divider />
                  <Box>
                    <Text fontWeight="semibold" color="gray.700" mb={1}>Boundary Conditions:</Text>
                    <Text color="gray.600" fontSize="md">
                      • <strong>Inlet (left):</strong> Prescribed post-shock state (rankine-hugoniot conditions)<br/>
                      • <strong>Outlet (right):</strong> Non-reflecting outflow (characteristic-based)<br/>
                      • <strong>Radial (axis):</strong> Axisymmetric boundary<br/>
                      • <strong>Far-field (top):</strong> Transmissive outflow
                    </Text>
                  </Box>
                </VStack>
              </Box>
            </VStack>
          </Box>

          <Divider />

          {/* Key Results */}
          <Box>
            <Heading size="xl" color="brand.500" mb={6}>
              Physics Insights & Results
            </Heading>
            
            <VStack spacing={6} align="stretch">
              <Box bg="red.50" p={6} borderRadius="xl" border="2px" borderColor="red.200">
                <Heading size="md" color="red.800" mb={4}>
                  Shock Dynamics & Pressure Amplification
                </Heading>
                <Text fontSize="md" color="red.900" lineHeight="tall" mb={4}>
                  When the incident shock (M = 2.4, ~8 bar in air) impacts the water interface, three key phenomena occur simultaneously:
                </Text>
                <List spacing={3}>
                  <ListItem display="flex" alignItems="flex-start">
                    <ListIcon as={FaCheckCircle} color="red.500" mt={1} boxSize={5} />
                    <Text fontSize="md" color="red.900" lineHeight="tall">
                      <strong>Transmission:</strong> A stronger shock transmits into the water due to impedance matching (Z_water ≈ 830× Z_air), reaching <strong>~120 MPa peak pressure</strong>
                    </Text>
                  </ListItem>
                  <ListItem display="flex" alignItems="flex-start">
                    <ListIcon as={FaCheckCircle} color="red.500" mt={1} boxSize={5} />
                    <Text fontSize="md" color="red.900" lineHeight="tall">
                      <strong>Reflection:</strong> A weaker reflected shock bounces back into the air, partially relieving the incident pressure
                    </Text>
                  </ListItem>
                  <ListItem display="flex" alignItems="flex-start">
                    <ListIcon as={FaCheckCircle} color="red.500" mt={1} boxSize={5} />
                    <Text fontSize="md" color="red.900" lineHeight="tall">
                      <strong>Focusing:</strong> Geometric focusing at the cavity apex creates local pressure spikes exceeding <strong>150 MPa</strong> — sufficient to cause material erosion or structural damage
                    </Text>
                  </ListItem>
                </List>
              </Box>

              <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor}>
                <Heading size="md" color="gray.800" mb={4}>
                  Cavity Deformation & Jetting
                </Heading>
                <Text fontSize="md" color="gray.700" lineHeight="tall" mb={4}>
                  At t = 0.36 µs (snapshot time), the cavity has begun asymmetric collapse:
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                  <Box>
                    <Text fontWeight="semibold" color="gray.800" mb={2}>Upstream (shock-facing) side:</Text>
                    <Text color="gray.600" fontSize="md" lineHeight="tall">
                      The interface flattens and begins inversion as the transmitted shock drives a high-velocity water jet inward. Peak jet velocities reach <strong>~480 m/s</strong>, creating extreme shear and turbulence at the jet tip.
                    </Text>
                  </Box>
                  <Box>
                    <Text fontWeight="semibold" color="gray.800" mb={2}>Downstream side:</Text>
                    <Text color="gray.600" fontSize="md" lineHeight="tall">
                      The cavity expands slightly due to rarefaction wave interaction before eventually collapsing. This asymmetry is critical for predicting droplet breakup and spray formation in practical applications.
                    </Text>
                  </Box>
                </SimpleGrid>
              </Box>

              <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor}>
                <Heading size="md" color="gray.800" mb={4}>
                  Energy Partitioning
                </Heading>
                <Text fontSize="md" color="gray.700" lineHeight="tall" mb={4}>
                  Integrated energy budget at t = 0.36 µs reveals:
                </Text>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                  <Box bg={sectionBg} p={4} borderRadius="lg" textAlign="center">
                    <Text fontSize="3xl" fontWeight="bold" color="brand.500">~62%</Text>
                    <Text fontSize="sm" color="gray.600" mt={2}>Internal Energy (compression)</Text>
                  </Box>
                  <Box bg={sectionBg} p={4} borderRadius="lg" textAlign="center">
                    <Text fontSize="3xl" fontWeight="bold" color="orange.500">~31%</Text>
                    <Text fontSize="sm" color="gray.600" mt={2}>Kinetic Energy (flow motion)</Text>
                  </Box>
                  <Box bg={sectionBg} p={4} borderRadius="lg" textAlign="center">
                    <Text fontSize="3xl" fontWeight="bold" color="purple.500">~7%</Text>
                    <Text fontSize="sm" color="gray.600" mt={2}>Surface Energy & dissipation</Text>
                  </Box>
                </SimpleGrid>
              </Box>
            </VStack>
          </Box>

          <Divider />

          {/* Key Features */}
          <Box bg={cardBg} p={8} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor}>
            <Heading size="lg" color="brand.500" mb={6}>
              Key Numerical Capabilities Demonstrated
            </Heading>
            <List spacing={4}>
              <ListItem display="flex" alignItems="flex-start">
                <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                <Text fontSize="lg" color="gray.700" lineHeight="tall">
                  <strong>Fully compressible, multi-fluid modeling</strong> — handles air–water system with 1000:1 density ratio and extreme pressure gradients
                </Text>
              </ListItem>
              <ListItem display="flex" alignItems="flex-start">
                <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                <Text fontSize="lg" color="gray.700" lineHeight="tall">
                  <strong>Axisymmetric formulation</strong> — realistic 3D physics with 2D computational cost for cavities and droplets
                </Text>
              </ListItem>
              <ListItem display="flex" alignItems="flex-start">
                <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                <Text fontSize="lg" color="gray.700" lineHeight="tall">
                  <strong>Accurate shock representation</strong> — sharp fronts, correct jump conditions, and stable long-time integration
                </Text>
              </ListItem>
              <ListItem display="flex" alignItems="flex-start">
                <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                <Text fontSize="lg" color="gray.700" lineHeight="tall">
                  <strong>Built-in physics</strong> — surface tension, gas-liquid coupling, and non-linear acoustics without user-defined functions
                </Text>
              </ListItem>
              <ListItem display="flex" alignItems="flex-start">
                <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                <Text fontSize="lg" color="gray.700" lineHeight="tall">
                  <strong>GPU acceleration</strong> — 52× speedup enables parametric sweeps and design optimization
                </Text>
              </ListItem>
            </List>
          </Box>

          {/* Industrial Applications */}
          <Box>
            <Heading size="lg" color="brand.500" mb={6} textAlign="center">
              Industrial Applications
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <Box 
                bg={cardBg}
                p={6}
                borderRadius="xl"
                shadow="md"
                border="1px"
                borderColor={borderColor}
                _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
                transition="all 0.2s"
              >
                <VStack spacing={3} align="start">
                  <Icon as={FaTint} boxSize={8} color="blue.500" />
                  <Heading size="md" color="gray.800">
                    Cavitation & Water Hammer Analysis
                  </Heading>
                  <Text color="gray.600" fontSize="md" lineHeight="tall">
                    Predict damage zones in hydraulic systems, pipelines, and pumps. Model transient pressure surges, bubble collapse loads, and erosion risk. Design protective measures and validate relief valve sizing.
                  </Text>
                </VStack>
              </Box>

              <Box 
                bg={cardBg}
                p={6}
                borderRadius="xl"
                shadow="md"
                border="1px"
                borderColor={borderColor}
                _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
                transition="all 0.2s"
              >
                <VStack spacing={3} align="start">
                  <Icon as={FaCar} boxSize={8} color="orange.500" />
                  <Heading size="md" color="gray.800">
                    Fuel Spray & Droplet Impact Dynamics
                  </Heading>
                  <Text color="gray.600" fontSize="md" lineHeight="tall">
                    Optimize fuel injector design for combustion engines. Model droplet breakup, secondary atomization, and wall impingement. Reduce emissions and improve combustion efficiency through better spray control.
                  </Text>
                </VStack>
              </Box>

              <Box 
                bg={cardBg}
                p={6}
                borderRadius="xl"
                shadow="md"
                border="1px"
                borderColor={borderColor}
                _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
                transition="all 0.2s"
              >
                <VStack spacing={3} align="start">
                  <Icon as={FaRocket} boxSize={8} color="red.500" />
                  <Heading size="md" color="gray.800">
                    Shock-Induced Phase Interaction in Propulsion
                  </Heading>
                  <Text color="gray.600" fontSize="md" lineHeight="tall">
                    Analyze shock-droplet interactions in rocket engines, scramjets, and pulse detonation systems. Understand mixture preparation, ignition enhancement, and unstable combustion mechanisms.
                  </Text>
                </VStack>
              </Box>

              <Box 
                bg={cardBg}
                p={6}
                borderRadius="xl"
                shadow="md"
                border="1px"
                borderColor={borderColor}
                _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
                transition="all 0.2s"
              >
                <VStack spacing={3} align="start">
                  <Icon as={FaFlask} boxSize={8} color="purple.500" />
                  <Heading size="md" color="gray.800">
                    High-Speed Imaging & Experimental Correlation
                  </Heading>
                  <Text color="gray.600" fontSize="md" lineHeight="tall">
                    Generate synthetic shadowgraphs and schlieren images for direct comparison with shock tube and ballistic range experiments. Validate models, extract physics, and plan test campaigns.
                  </Text>
                </VStack>
              </Box>
            </SimpleGrid>
          </Box>

          {/* Business Value */}
          <Box 
            bg="brand.500" 
            color="white" 
            p={8} 
            borderRadius="xl" 
            shadow="xl"
            position="relative"
            overflow="hidden"
          >
            <Box position="relative" zIndex={1}>
              <HStack spacing={3} mb={4}>
                <Icon as={FaDollarSign} boxSize={8} />
                <Heading size="lg">Business Value & ROI</Heading>
              </HStack>
              <Text fontSize="lg" lineHeight="tall" mb={4}>
                NumericalAI turns specialist, HPC-only shock physics simulation into a cloud-accessible workflow: upload geometry, run MFC, visualize multi-field results — all without buying hardware or hiring PhD-level CFD staff.
              </Text>
              <Text fontSize="lg" lineHeight="tall" mb={4}>
                <strong>Cost Savings:</strong> Each simulation replaces $20K–$50K in shock tube or ballistic range testing. Run 10–100× more cases for design optimization and uncertainty quantification.
              </Text>
              <Text fontSize="lg" lineHeight="tall">
                <strong>Speed to Insight:</strong> From concept to validated design in weeks instead of months. Accelerate product development, reduce prototype failures, and get to market faster.
              </Text>
            </Box>
            
            {/* Decorative background */}
            <Box
              position="absolute"
              top="-50%"
              right="-10%"
              width="300px"
              height="300px"
              borderRadius="full"
              bg="whiteAlpha.100"
              zIndex={0}
            />
          </Box>

          {/* Call to Action */}
          <Box textAlign="center" py={8}>
            <VStack spacing={4}>
              <Heading size="md" color="gray.700">
                Ready to Solve Your Shock-Fluid Interaction Problems?
              </Heading>
              <Text color="gray.600" fontSize="lg" maxW="2xl">
                Run high-fidelity multiphase shock simulations on NumericalAI's cloud platform. No shock tube required — just upload your case and let our GPUs do the work.
              </Text>
              <HStack spacing={4} mt={4}>
                <Button
                  colorScheme="brand"
                  size="lg"
                  onClick={() => navigate('/start-simulation')}
                >
                  Start Your Simulation
                </Button>
                <Button
                  variant="outline"
                  colorScheme="brand"
                  size="lg"
                  onClick={() => navigate('/dashboard')}
                >
                  Go to Dashboard
                </Button>
              </HStack>
            </VStack>
          </Box>
        </VStack>
      </Container>

      <Footer />
    </Box>
  );
};

export default ShockWaterCavityInteraction;